import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
  } from "react";
  import { useAuth } from "../contexts/AuthContext";
  
  const CreditsContext = createContext(null);
  
  export function CreditsProvider({
    children,
    initialScreening = 0,
    initialPass = 0,
    initialShortlistPass = 0,
    storageKey = "exodia-credits-v1",
  }) {
    const { currentUser, getUserCredits, updateUserCredits, createTransaction, getUserTransactions } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    
    // Create user-specific storage key
    const userStorageKey = useMemo(() => {
      return currentUser ? `${storageKey}-user-${currentUser.uid}` : storageKey;
    }, [currentUser, storageKey]);
    
    // Load persisted state (if any) - fallback for when user is not logged in
    const [state, setState] = useState(() => {
      console.log('🔄 CreditsContext initializing with initialScreening:', initialScreening, 'initialPass:', initialPass, 'initialShortlistPass:', initialShortlistPass);
      if (typeof window !== "undefined") {
        try {
          // Use the current user-specific key or fallback to default
          const keyToUse = currentUser ? `${storageKey}-user-${currentUser.uid}` : storageKey;
          const raw = localStorage.getItem(keyToUse);
          if (raw) {
            const data = JSON.parse(raw);
            console.log('📱 Loaded from localStorage:', data);
            return {
              screeningCredits: Number.isFinite(data?.screeningCredits)
                ? data.screeningCredits
                : initialScreening,
              talentRolePass: Number.isFinite(data?.talentRolePass)
                ? data.talentRolePass
                : initialPass,
              shortlistPass: Number.isFinite(data?.shortlistPass)
                ? data.shortlistPass
                : initialShortlistPass,
              unlockedTalentIds: Array.isArray(data?.unlockedTalentIds)
                ? data.unlockedTalentIds
                : [],
              transactions: Array.isArray(data?.transactions)
                ? data.transactions
                : [],
            };
          }
        } catch (error) {
          console.error('❌ Error loading from localStorage:', error);
        }
      }
      console.log('🆕 Using default values:', { screeningCredits: initialScreening, talentRolePass: initialPass, shortlistPass: initialShortlistPass });
      return {
        screeningCredits: initialScreening,
        talentRolePass: initialPass,
        shortlistPass: initialShortlistPass,
        unlockedTalentIds: [],
        transactions: [],
      };
    });
  
    // Load transactions from database
    const loadTransactionsFromDB = useCallback(async (userId) => {
      if (!getUserTransactions) return [];
      
      try {
        const result = await getUserTransactions(userId);
        if (result.status === 'success' && result.data) {
          // Transform database format to local format
          return result.data.map(tx => ({
            id: tx.transaction_id,
            createdAt: tx.created_at,
            action: tx.action,
            type: tx.type,
            creditsDelta: tx.credits_delta,
            passesDelta: tx.passes_delta,
            creditsAfter: tx.credits_after,
            passesAfter: tx.passes_after,
            totalCost: tx.total_cost ? tx.total_cost / 100 : null,
            note: tx.note
          }));
        }
      } catch (error) {
        console.error('Failed to load transactions from database:', error);
      }
      return [];
    }, [getUserTransactions]);
  
    // Save transaction to database
    const saveTransactionToDB = useCallback(async (transactionData) => {
      if (!currentUser || !createTransaction) return;
      
      try {
        await createTransaction({
          user_id: currentUser.uid,
          transaction_id: transactionData.id,
          action: transactionData.action,
          type: transactionData.type,
          creditsDelta: transactionData.creditsDelta,
          passesDelta: transactionData.passesDelta,
          creditsAfter: transactionData.creditsAfter,
          passesAfter: transactionData.passesAfter,
          totalCost: transactionData.totalCost !== null && transactionData.totalCost !== undefined ? Math.round(Number(transactionData.totalCost) * 100) : null,
          note: transactionData.note,
          createdAt: new Date(transactionData.createdAt).toISOString().slice(0, 19).replace('T', ' ')
        });
      } catch (error) {
        console.error('Failed to save transaction to database:', error);
      }
    }, [currentUser, createTransaction]);
  
    // Handle user changes (login/logout) and load user-specific data
    useEffect(() => {
      const loadUserCredits = async () => {
        if (currentUser && getUserCredits) {
          setIsLoading(true);
          try {
            const credits = await getUserCredits(currentUser.uid);
            const transactions = await loadTransactionsFromDB(currentUser.uid);
            
            setState(prev => ({
              ...prev,
              screeningCredits: credits.screeningCredits,
              talentRolePass: credits.talentRolePass,
              shortlistPass: credits.shortlistPass,
              unlockedTalentIds: credits.unlockedTalentIds || [],
              transactions: transactions.length > 0 ? transactions : prev.transactions,
            }));
          } catch (error) {
            console.error('Failed to load user credits:', error);
            // Fallback to user-specific localStorage
            try {
              const keyToUse = `${storageKey}-user-${currentUser.uid}`;
              const raw = localStorage.getItem(keyToUse);
              if (raw) {
                const data = JSON.parse(raw);
                setState(prev => ({
                  ...prev,
                  screeningCredits: data.screeningCredits || prev.screeningCredits,
                  talentRolePass: data.talentRolePass || prev.talentRolePass,
                  shortlistPass: data.shortlistPass || prev.shortlistPass,
                  unlockedTalentIds: data.unlockedTalentIds || [],
                }));
              }
            } catch (localError) {
              console.error('Failed to load from localStorage:', localError);
            }
          } finally {
            setIsLoading(false);
          }
        } else if (!currentUser) {
          // User logged out, reset to default state
          setState({
            screeningCredits: initialScreening,
            talentRolePass: initialPass,
            shortlistPass: initialShortlistPass,
            unlockedTalentIds: [],
            transactions: [],
          });
        }
      };
  
      loadUserCredits();
    }, [currentUser, getUserCredits, storageKey, initialScreening, initialPass, initialShortlistPass]);
  
    // Persist
    useEffect(() => {
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(userStorageKey, JSON.stringify(state));
        }
      } catch {
        /* ignore */
      }
    }, [state, userStorageKey]);
  
    // Helpers
    const unlockedSet = useMemo(
      () => new Set(state.unlockedTalentIds.map(String)),
      [state.unlockedTalentIds]
    );
  
    const isTalentUnlocked = useCallback(
      (id) => unlockedSet.has(String(id)),
      [unlockedSet]
    );
  
    /**
     * Unlock using Talent Role Passes (logs with balance snapshot)
     */
    const unlockTalent = useCallback(async (id, cost = 1) => {
      const key = String(id);
      let success = false;
      let newUnlockedIds = [];
  
      setState((prev) => {
        const prevSet = new Set(prev.unlockedTalentIds.map(String));
        if (prevSet.has(key)) return prev;              // already unlocked
        if (Number(prev.talentRolePass) < Number(cost)) return prev;    // not enough passes
  
        prevSet.add(key);
        success = true;
        newUnlockedIds = Array.from(prevSet);
  
        const passesAfter = Number(prev.talentRolePass) - Number(cost);
        const entry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'unlock',
          type: 'Talent Role Pass',
          creditsDelta: 0,
          passesDelta: -cost,
          creditsAfter: undefined,
          passesAfter,
          talentId: id != null ? String(id) : undefined,
          talentName: undefined,
          note: 'Unlock talent profile',
        };
  
        return {
          ...prev,
          talentRolePass: passesAfter,
          unlockedTalentIds: newUnlockedIds,
          transactions: [entry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (success && currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, Number(state.screeningCredits), Number(state.talentRolePass) - Number(cost), Number(state.shortlistPass), newUnlockedIds);
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
  
      return success;
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass]);
  
    /**
     * Consume screening credits (logs with balance snapshot)
     */
    const consumeScreening = useCallback(async (amount = 1) => {
      let success = false;
      let transactionEntry = null;
  
      setState((prev) => {
        const available = Number(prev.screeningCredits ?? 0);
        if (available < Number(amount)) return prev;
  
        success = true;
        const creditsAfter = available - Number(amount);
        transactionEntry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'hire',
          type: 'Screening Credits',
          creditsDelta: -amount,
          passesDelta: 0,
          creditsAfter,
          passesAfter: undefined,
          note: 'Consume screening credit',
        };
  
        return {
          ...prev,
          screeningCredits: creditsAfter,
          transactions: [transactionEntry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (success && currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, Number(state.screeningCredits) - Number(amount), Number(state.talentRolePass), Number(state.shortlistPass), state.unlockedTalentIds);
          // Save transaction to database
          if (transactionEntry) {
            await saveTransactionToDB(transactionEntry);
          }
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
  
      return success;
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass, saveTransactionToDB]);
  
    /**
     * Refund screening credits (logs with balance snapshot)
     */
    const refundScreening = useCallback(async (amount = 1) => {
      setState((prev) => {
        const creditsAfter = Number(prev.screeningCredits ?? 0) + Number(amount);
        const entry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'refund',
          type: 'Screening Credits',
          creditsDelta: amount,
          passesDelta: 0,
          creditsAfter,
          passesAfter: undefined,
          note: 'Refund screening credit',
        };
  
        return {
          ...prev,
          screeningCredits: creditsAfter,
          transactions: [entry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, state.screeningCredits + amount, state.talentRolePass, state.shortlistPass, state.unlockedTalentIds);
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass]);
  
    /**
     * Purchase screening credits (logs with balance snapshot)
     */
    const purchaseCredits = useCallback(async (amount, totalPrice) => {
      let transactionEntry = null;
  
      setState((prev) => {
        const creditsAfter = Number(prev.screeningCredits ?? 0) + Number(amount);
        transactionEntry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'purchase',
          type: 'Screening Credits',
          creditsDelta: amount,
          passesDelta: 0,
          creditsAfter,
          passesAfter: undefined,
          totalCost: totalPrice > 0 ? totalPrice : null,
          note: totalPrice === 0 ? `Received ${amount} free screening credits` : `Purchased ${amount} screening credits for $${totalPrice.toFixed(2)}`,
        };
  
        return {
          ...prev,
          screeningCredits: creditsAfter,
          transactions: [transactionEntry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, Number(state.screeningCredits) + Number(amount), Number(state.talentRolePass), Number(state.shortlistPass), state.unlockedTalentIds);
          // Save transaction to database
          if (transactionEntry) {
            await saveTransactionToDB(transactionEntry);
          }
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass, saveTransactionToDB]);
  
    /**
     * Purchase talent role passes (logs with balance snapshot)
     */
    const purchaseTalentRolePasses = useCallback(async (amount, totalPrice) => {
      let transactionEntry = null;
  
      setState((prev) => {
        const passesAfter = Number(prev.talentRolePass ?? 0) + Number(amount);
        transactionEntry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'purchase',
          type: 'Talent Role Pass',
          creditsDelta: 0,
          passesDelta: amount,
          creditsAfter: undefined,
          passesAfter,
          totalCost: totalPrice > 0 ? totalPrice : null,
          note: totalPrice === 0 ? `Received ${amount} free talent role passes` : `Purchased ${amount} talent role passes for $${totalPrice.toFixed(2)}`,
        };
  
        return {
          ...prev,
          talentRolePass: passesAfter,
          transactions: [transactionEntry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, Number(state.screeningCredits), Number(state.talentRolePass) + Number(amount), Number(state.shortlistPass), state.unlockedTalentIds);
          // Save transaction to database
          if (transactionEntry) {
            await saveTransactionToDB(transactionEntry);
          }
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass, saveTransactionToDB]);
  
    /**
     * Purchase shortlist passes (logs with balance snapshot)
     */
    const purchaseShortlistPasses = useCallback(async (amount, totalPrice) => {
      let transactionEntry = null;
  
      setState((prev) => {
        const passesAfter = Number(prev.shortlistPass ?? 0) + Number(amount);
        transactionEntry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'purchase',
          type: 'Shortlist Pass',
          creditsDelta: 0,
          passesDelta: amount,
          creditsAfter: undefined,
          passesAfter,
          totalCost: totalPrice > 0 ? totalPrice : null,
          note: totalPrice === 0 ? `Received ${amount} free shortlist passes` : `Purchased ${amount} shortlist passes for $${totalPrice.toFixed(2)}`,
        };
  
        return {
          ...prev,
          shortlistPass: passesAfter,
          transactions: [transactionEntry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, Number(state.screeningCredits), Number(state.talentRolePass), Number(state.shortlistPass) + Number(amount), state.unlockedTalentIds);
          // Save transaction to database
          if (transactionEntry) {
            await saveTransactionToDB(transactionEntry);
          }
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass, saveTransactionToDB]);
  
    /**
     * Consume shortlist pass (logs with balance snapshot)
     */
    const consumeShortlistPass = useCallback(async (amount = 1) => {
      let success = false;
      let transactionEntry = null;
  
      setState((prev) => {
        const available = Number(prev.shortlistPass ?? 0);
        if (available < Number(amount)) return prev;
  
        success = true;
        const passesAfter = available - Number(amount);
        transactionEntry = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          action: 'shortlist',
          type: 'Shortlist Pass',
          creditsDelta: 0,
          passesDelta: -amount,
          creditsAfter: undefined,
          passesAfter,
          note: 'Consume shortlist pass',
        };
  
        return {
          ...prev,
          shortlistPass: passesAfter,
          transactions: [transactionEntry, ...(prev.transactions || [])].slice(0, 500),
        };
      });
  
      // Update database if user is logged in
      if (success && currentUser && updateUserCredits) {
        try {
          await updateUserCredits(currentUser.uid, Number(state.screeningCredits), Number(state.talentRolePass), Number(state.shortlistPass) - Number(amount), state.unlockedTalentIds);
          // Save transaction to database
          if (transactionEntry) {
            await saveTransactionToDB(transactionEntry);
          }
        } catch (error) {
          console.error('Failed to update credits in database:', error);
        }
      }
  
      return success;
    }, [currentUser, updateUserCredits, state.screeningCredits, state.talentRolePass, state.shortlistPass, saveTransactionToDB]);
  
    // Optional external logger for other events (top-ups, purchases)
    const logTransaction = useCallback(async (tx) => {
      const entry = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
        action: tx?.action || 'adjust',
        type: tx?.type || '—',
        creditsDelta: Number(tx?.creditsDelta || 0),
        passesDelta: Number(tx?.passesDelta || 0),
        creditsAfter: Number.isFinite(tx?.creditsAfter) ? tx.creditsAfter : undefined,
        passesAfter: Number.isFinite(tx?.passesAfter) ? tx.passesAfter : undefined,
        totalCost: Number.isFinite(tx?.totalCost) && tx.totalCost > 0 ? tx.totalCost : undefined,
        note: tx?.note || undefined,
      };
      setState((prev) => ({
        ...prev,
        transactions: [entry, ...(prev.transactions || [])].slice(0, 500),
      }));
  
      // Save transaction to database
      if (currentUser && saveTransactionToDB) {
        try {
          await saveTransactionToDB(entry);
        } catch (error) {
          console.error('Failed to save transaction to database:', error);
        }
      }
    }, [currentUser, saveTransactionToDB]);
  
    const value = useMemo(
      () => ({
        screeningCredits: state.screeningCredits,
        talentRolePass: state.talentRolePass,
        shortlistPass: state.shortlistPass,
        isTalentUnlocked,
        unlockTalent,
        transactions: state.transactions || [],
        logTransaction,
        consumeScreening,
        refundScreening,
        consumeShortlistPass,
        purchaseCredits,
        purchaseTalentRolePasses,
        purchaseShortlistPasses,
        isLoading,
      }),
      [
        state.screeningCredits,
        state.talentRolePass,
        state.shortlistPass,
        state.transactions,
        isTalentUnlocked,
        unlockTalent,
        logTransaction,
        consumeScreening,
        refundScreening,
        consumeShortlistPass,
        purchaseCredits,
        purchaseTalentRolePasses,
        purchaseShortlistPasses,
        isLoading,
      ]
    );
  
    return (
      <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>
    );
  }
  
  export function useCredits() {
    const ctx = useContext(CreditsContext);
    if (!ctx) throw new Error("useCredits must be used within a CreditsProvider");
    return ctx;
  }
  